import { supabase } from '../lib/supabase';
import { logger } from './loggingService';
// Stripe integration handled via edge functions (update-subscription-quantity)

export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo_url?: string;
  website?: string;
  stripe_customer_id?: string;
  billing_email?: string;
  owner_id: string;
  settings?: any;
  metadata?: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  permissions?: any;
  is_active: boolean;
  joined_at: string;
  updated_at: string;
  user?: {
    email: string;
    full_name?: string;
  };
}

export interface OrganizationInvitation {
  id: string;
  organization_id: string;
  invited_by: string;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  token: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'revoked';
  expires_at: string;
  accepted_at?: string;
  accepted_by?: string;
  created_at: string;
  updated_at: string;
}

export interface OrganizationSubscription {
  id: string;
  organization_id: string;
  plan_id: string;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  seats_total: number;
  seats_used: number;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'paused';
  billing_cycle: 'monthly' | 'yearly';
  price_per_seat: number;
  current_period_start?: string;
  current_period_end?: string;
  trial_start?: string;
  trial_end?: string;
  cancel_at_period_end: boolean;
  canceled_at?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export class OrganizationService {
  /**
   * Create a new organization
   */
  static async createOrganization(data: {
    name: string;
    slug: string;
    description?: string;
    userId: string;
  }): Promise<{ organization: Organization | null; error: string | null }> {
    try {
      // Check if slug is available
      const { data: existing } = await supabase
        .from('organizations')
        .select('id')
        .eq('slug', data.slug)
        .maybeSingle();

      if (existing) {
        return { organization: null, error: 'This organization name is already taken' };
      }

      // Create organization
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: data.name,
          slug: data.slug,
          description: data.description,
          owner_id: data.userId,
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Add owner as first member
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: org.id,
          user_id: data.userId,
          role: 'owner',
        });

      if (memberError) throw memberError;

      return { organization: org, error: null };
    } catch (error) {
      logger.error('Error creating organization:', error);
      return {
        organization: null,
        error: error instanceof Error ? error.message : 'Failed to create organization',
      };
    }
  }

  /**
   * Get user's organizations
   */
  static async getUserOrganizations(userId: string): Promise<Organization[]> {
    try {
      const { data, error } = await supabase
        .from('user_organizations')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error fetching organizations:', error);
      return [];
    }
  }

  /**
   * Get organization by ID
   */
  static async getOrganization(organizationId: string): Promise<Organization | null> {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', organizationId)
        .maybeSingle();

      if (error) {
        logger.error('Error fetching organization', { error, organizationId });
        throw error;
      }

      if (!data) {
        throw new Error('Organization not found');
      }

      return data;
    } catch (error) {
      logger.error('Error fetching organization:', error);
      return null;
    }
  }

  /**
   * Get organization members
   */
  static async getMembers(organizationId: string): Promise<OrganizationMember[]> {
    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          *,
          user:user_id (
            email,
            full_name
          )
        `)
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('joined_at', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error fetching members', { error, organizationId });
      return [];
    }
  }

  /**
   * Invite member to organization
   */
  static async inviteMember(data: {
    organizationId: string;
    email: string;
    role: 'admin' | 'member' | 'viewer';
    invitedBy: string;
  }): Promise<{ success: boolean; error: string | null }> {
    try {
      // Enterprise-grade atomic check: Get user and check membership in single query to prevent race condition
      const { data: user } = await supabase
        .from('users')
        .select('id')
        .eq('email', data.email)
        .maybeSingle();

      const userId = user?.id;

      // Atomic check: If user exists, check membership status in one query
      let existingMember = null;
      if (userId) {
        const { data: memberData } = await supabase
          .from('organization_members')
          .select('id')
          .eq('organization_id', data.organizationId)
          .eq('user_id', userId)
          .eq('is_active', true)
          .maybeSingle();
        existingMember = memberData;
      }

      if (existingMember) {
        return { success: false, error: 'User is already a member of this organization' };
      }

      // Check if there's a pending invitation (atomic check for invitation)
      const { data: existingInvite } = await supabase
        .from('organization_invitations')
        .select('id')
        .eq('organization_id', data.organizationId)
        .eq('email', data.email)
        .eq('status', 'pending')
        .maybeSingle();

      if (existingInvite) {
        return { success: false, error: 'An invitation has already been sent to this email' };
      }

      // Check seat availability
      const canAdd = await this.canAddSeat(data.organizationId);
      if (!canAdd) {
        return { success: false, error: 'No available seats. Please upgrade your plan.' };
      }

      // Generate invitation token
      const invitationToken = crypto.randomUUID();

      // Create invitation with upsert to handle race conditions (atomic operation)
      // Use onConflict to prevent duplicate invitations if two requests come in simultaneously
      const { data: invitation, error: inviteError } = await supabase
        .from('organization_invitations')
        .upsert({
          organization_id: data.organizationId,
          email: data.email,
          role: data.role,
          invited_by: data.invitedBy,
          token: invitationToken,
          status: 'pending',
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
        }, {
          onConflict: 'organization_id,email', // Prevent duplicate invitations
        })
        .select('token')
        .maybeSingle();

      if (inviteError) {
        // Check if error is due to duplicate (race condition caught by constraint)
        if (inviteError.code === '23505') { // Unique violation
          return { success: false, error: 'An invitation has already been sent to this email' };
        }
        throw inviteError;
      }

      if (!invitation) {
        throw new Error('Failed to create invitation');
      }

      // Get organization details for email
      const { data: organization } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', data.organizationId)
        .maybeSingle();

      // Get inviter details
      const { data: inviter } = await supabase
        .from('users')
        .select('full_name, email')
        .eq('id', data.invitedBy)
        .maybeSingle();

      const inviterName = inviter?.full_name || inviter?.email || 'Someone';

      // Send invitation email
      const { EmailService } = await import('./emailService');
      const emailResult = await EmailService.sendInvitationEmail(
        data.email,
        organization?.name || 'Organization',
        invitation.token,
        data.role,
        inviterName
      );

      if (!emailResult.success) {
        logger.warn('Failed to send invitation email, but invitation was created', {
          error: emailResult.error,
          email: data.email,
          organizationId: data.organizationId,
        });
        // Don't fail the invitation if email fails - invitation is still created
      }

      return { success: true, error: null };
    } catch (error) {
      logger.error('Error inviting member', { error, organizationId: data.organizationId, email: data.email });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send invitation',
      };
    }
  }

  /**
   * Accept invitation
   */
  static async acceptInvitation(token: string, userId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      // Get invitation
      const { data: invitation, error: inviteError } = await supabase
        .from('organization_invitations')
        .select('*')
        .eq('token', token)
        .eq('status', 'pending')
        .maybeSingle();

      if (inviteError) {
        logger.error('Error fetching invitation', { error: inviteError, token });
        return { success: false, error: `Failed to fetch invitation: ${inviteError.message}` };
      }

      if (!invitation) {
        return { success: false, error: 'Invalid or expired invitation' };
      }

      // Check if expired
      if (new Date(invitation.expires_at) < new Date()) {
        await supabase
          .from('organization_invitations')
          .update({ status: 'expired' })
          .eq('id', invitation.id);
        return { success: false, error: 'This invitation has expired' };
      }

      // Check seat availability
      const canAdd = await this.canAddSeat(invitation.organization_id);
      if (!canAdd) {
        return { success: false, error: 'No available seats in this organization' };
      }

      // Add member
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: invitation.organization_id,
          user_id: userId,
          role: invitation.role,
        });

      if (memberError) throw memberError;

      // Update invitation status
      await supabase
        .from('organization_invitations')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString(),
          accepted_by: userId,
        })
        .eq('id', invitation.id);

      return { success: true, error: null };
    } catch (error) {
      logger.error('Error accepting invitation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to accept invitation',
      };
    }
  }

  /**
   * Remove member from organization
   */
  static async removeMember(organizationId: string, userId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      // Check if user is the owner
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('owner_id')
        .eq('id', organizationId)
        .maybeSingle();

      if (orgError) {
        logger.error('Error fetching organization for member removal', { error: orgError, organizationId });
        return { success: false, error: `Failed to fetch organization: ${orgError.message}` };
      }

      if (!org) {
        return { success: false, error: 'Organization not found' };
      }

      if (org.owner_id === userId) {
        return { success: false, error: 'Cannot remove the organization owner' };
      }

      // Remove member
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('organization_id', organizationId)
        .eq('user_id', userId);

      if (error) throw error;

      return { success: true, error: null };
    } catch (error) {
      logger.error('Error removing member:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to remove member',
      };
    }
  }

  /**
   * Update member role
   */
  static async updateMemberRole(
    organizationId: string,
    userId: string,
    role: 'admin' | 'member' | 'viewer'
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ role })
        .eq('organization_id', organizationId)
        .eq('user_id', userId);

      if (error) throw error;

      return { success: true, error: null };
    } catch (error) {
      logger.error('Error updating member role:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update role',
      };
    }
  }

  /**
   * Get organization subscription
   */
  static async getSubscription(organizationId: string): Promise<OrganizationSubscription | null> {
    try {
      const { data, error } = await supabase
        .from('organization_subscriptions')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('status', 'active')
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error fetching subscription:', error);
      return null;
    }
  }

  /**
   * Check if organization can add more seats
   */
  static async canAddSeat(organizationId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('can_add_organization_seat', {
        org_id: organizationId,
      });

      if (error) throw error;
      return data || false;
    } catch (error) {
      logger.error('Error checking seat availability:', error);
      return false;
    }
  }

  /**
   * Add seats to organization subscription
   */
  static async addSeats(
    organizationId: string,
    additionalSeats: number
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const subscription = await this.getSubscription(organizationId);
      if (!subscription) {
        return { success: false, error: 'No active subscription found' };
      }

      if (!subscription.stripe_subscription_id) {
        return { success: false, error: 'Stripe subscription not found' };
      }

      // Update Stripe subscription quantity
      const newQuantity = subscription.seats_total + additionalSeats;

      // Call Stripe API via edge function
      const { error } = await supabase.functions.invoke('update-subscription-quantity', {
        body: {
          subscriptionId: subscription.stripe_subscription_id,
          quantity: newQuantity,
        },
      });

      if (error) throw error;

      // Update database
      await supabase
        .from('organization_subscriptions')
        .update({
          seats_total: newQuantity,
          updated_at: new Date().toISOString(),
        })
        .eq('id', subscription.id);

      return { success: true, error: null };
    } catch (error) {
      logger.error('Error adding seats:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to add seats',
      };
    }
  }

  /**
   * Remove unused seats
   */
  static async removeSeats(
    organizationId: string,
    seatsToRemove: number
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const subscription = await this.getSubscription(organizationId);
      if (!subscription) {
        return { success: false, error: 'No active subscription found' };
      }

      const newQuantity = subscription.seats_total - seatsToRemove;

      if (newQuantity < subscription.seats_used) {
        return {
          success: false,
          error: `Cannot remove seats. ${subscription.seats_used} seats are currently in use.`,
        };
      }

      if (!subscription.stripe_subscription_id) {
        return { success: false, error: 'Stripe subscription not found' };
      }

      // Update Stripe subscription quantity
      const { error } = await supabase.functions.invoke('update-subscription-quantity', {
        body: {
          subscriptionId: subscription.stripe_subscription_id,
          quantity: newQuantity,
        },
      });

      if (error) throw error;

      // Update database
      await supabase
        .from('organization_subscriptions')
        .update({
          seats_total: newQuantity,
          updated_at: new Date().toISOString(),
        })
        .eq('id', subscription.id);

      return { success: true, error: null };
    } catch (error) {
      logger.error('Error removing seats:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to remove seats',
      };
    }
  }

  /**
   * Generate slug from organization name
   */
  static generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}
